import {
  OrderListParameterModifierType_supplier,
  OrderPaymentStatusType,
  OrderPaymentType,
  OrderListSortModifierType,
  OrderStatusesEnumType,
} from "@sellar-io/api-v2/types";
import { FilterType, SortType } from "../../../../lists/types";
import { IconType } from "@sellar-io/components";
import dayjs from "dayjs";
export type OrderListSearchType = "name" | "orderNumber";

export const OrderListSearchValues: FilterType<
  OrderListParameterModifierType_supplier,
  OrderListSearchType
> = {
  name: {
    key: "name",
    name: "Name",
    mutation: (value: string) => [
      {
        type: "name",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
  orderNumber: {
    key: "orderNumber",
    name: "Order #",
    mutation: (value: string) => [
      {
        type: "orderNumber",
        modifier: "EQUALS",
        value,
      },
    ],
  },
};
export type OrderStatusFilterType =
  | ""
  | "accepted"
  | "finalised"
  | "dispatched"
  | "delivered"
  | "cancelled";

export const OrderStatusFilterValues: FilterType<
  OrderListParameterModifierType_supplier,
  OrderStatusFilterType
> = {
  "": {
    key: "",
    name: "All statuses",
    mutation: () => [],
  },
  accepted: {
    key: "accepted",
    name: "Accepted",
    mutation: () =>
      [
        {
          type: "confirmedAt",
          modifier: "IS_NULL",
        },
        {
          type: "acceptedAt",
          modifier: "NOT_NULL",
        },
        {
          type: "cancelledAt",
          modifier: "IS_NULL",
        },
        {
          type: "deniedAt",
          modifier: "IS_NULL",
        },
      ] as const,
  },
  finalised: {
    key: "finalised",
    name: "Finalised",
    mutation: () =>
      [
        {
          type: "confirmedAt",
          modifier: "NOT_NULL",
        },
        {
          type: "cancelledAt",
          modifier: "IS_NULL",
        },
        {
          type: "deniedAt",
          modifier: "IS_NULL",
        },
        { type: "dispatchedAt", modifier: "IS_NULL" },
        { type: "deliveredAt", modifier: "IS_NULL" },
      ] as const,
  },
  dispatched: {
    key: "dispatched",
    name: "Dispatched",
    mutation: () =>
      [
        {
          type: "dispatchedAt",
          modifier: "NOT_NULL",
        },
        {
          type: "deliveredAt",
          modifier: "IS_NULL",
        },
        {
          type: "cancelledAt",
          modifier: "IS_NULL",
        },
        {
          type: "deniedAt",
          modifier: "IS_NULL",
        },
      ] as const,
  },
  delivered: {
    key: "delivered",
    name: "Delivered",
    mutation: () =>
      [
        {
          type: "deliveredAt",
          modifier: "NOT_NULL",
        },
        {
          type: "cancelledAt",
          modifier: "IS_NULL",
        },
        {
          type: "deniedAt",
          modifier: "IS_NULL",
        },
      ] as const,
  },
  cancelled: {
    key: "cancelled",
    name: "Cancelled",
    mutation: () =>
      [
        {
          type: "cancelledAt",
          modifier: "NOT_NULL",
        },
      ] as const,
  },
};

type OrderStatusValuesType<Type extends string | number> = {
  [K in Type]: {
    name: string;
    key: K;
    icon: IconType;
  };
};

export const OrderStatusesFilterValues: OrderStatusValuesType<OrderStatusesEnumType> =
  {
    accepted: {
      key: "accepted",
      name: "Accepted",
      icon: "THUMB_UP_OUTLINE",
    },
    finalised: {
      key: "finalised",
      name: "Finalised",
      icon: "CHECK_CIRCLE",
    },
    dispatched: {
      key: "dispatched",
      name: "Dispatched",
      icon: "PACKAGE_VARIANT_CLOSED",
    },
    delivered: {
      key: "delivered",
      name: "Delivered",
      icon: "CHECK_ALL",
    },
    cancelled: {
      key: "cancelled",
      name: "Cancelled",
      icon: "CLOSE",
    },
    voided: {
      key: "voided",
      name: "Voided",
      icon: "CLOSE",
    },
  };

export type FulfillmentFilterType =
  | ""
  | "direct"
  | "courier"
  | "haulier"
  | "sellarDistribution"
  | "collection"
  | "ups"
  | "apc"
  | "parcelforce"
  | "dx"
  | "dxDropOff";

export const OrderFulfillmentFilterValues: FilterType<
  OrderListParameterModifierType_supplier,
  FulfillmentFilterType
> = {
  "": {
    key: "",
    name: "All deliveries",
    mutation: () => [],
  },
  direct: {
    key: "direct",
    name: "Direct",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "direct",
        },
      ] as const,
  },
  courier: {
    key: "courier",
    name: "Courier",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "courier",
        },
      ] as const,
  },
  haulier: {
    key: "haulier",
    name: "Pallet",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "haulier",
        },
      ] as const,
  },
  sellarDistribution: {
    key: "sellarDistribution",
    name: "Sellar Distribution",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "sellarDistribution",
        },
      ] as const,
  },
  collection: {
    key: "collection",
    name: "Collection",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "collection",
        },
      ] as const,
  },
  ups: {
    key: "ups",
    name: "Courier UPS",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "ups",
        },
      ] as const,
  },
  apc: {
    key: "apc",
    name: "Courier APC",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "apc",
        },
      ] as const,
  },
  parcelforce: {
    key: "parcelforce",
    name: "Courier Parcelforce",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "parcelforce",
        },
      ] as const,
  },
  dx: {
    key: "dx",
    name: "Courier DX (collection)",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "dx",
        },
      ] as const,
  },
  dxDropOff: {
    key: "dxDropOff",
    name: "Courier DX (Drop Off Point)",
    mutation: () =>
      [
        {
          type: "fulfillmentType",
          modifier: "EQUALS",
          value: "dxDropOff",
        },
      ] as const,
  },
};

export type PaymentStatusType = "" | Exclude<OrderPaymentStatusType, "draft">;

const now = dayjs().toDate();

export const OrderPaymentStatusValues: FilterType<
  OrderListParameterModifierType_supplier,
  PaymentStatusType | "overdue"
> = {
  "": {
    key: "",
    name: "All statuses",
    mutation: () => [],
  },
  void: {
    key: "void",
    name: "Invoice cancelled",
    mutation: () => [
      {
        type: "orderStatuses" as const,
        modifier: "EQUALS" as const,
        value: ["cancelled" as const],
      },
    ],
  },
  open: {
    key: "open",
    name: "Invoice sent",
    mutation: () =>
      [
        {
          type: "paymentStatus",
          modifier: "EQUALS",
          value: "open",
        },
      ] as const,
  },
  paid: {
    key: "paid",
    name: "Paid",
    mutation: () =>
      [
        {
          type: "paidAt",
          modifier: "NOT_NULL",
        },
      ] as const,
  },
  refunded: {
    key: "refunded",
    name: "Invoice refunded",
    mutation: () =>
      [
        {
          type: "paymentStatus",
          modifier: "EQUALS",
          value: "refunded",
        },
      ] as const,
  },
  overdue: {
    key: "overdue",
    name: "Overdue",
    mutation: () =>
      [
        {
          type: "paymentType",
          modifier: "NOT_EQUALS",
          value: "sellarCredit",
        },
        {
          type: "acceptedAt",
          modifier: "NOT_NULL",
        },
        {
          type: "confirmedAt",
          modifier: "NOT_NULL",
        },
        {
          type: "cancelledAt",
          modifier: "IS_NULL",
        },
        {
          type: "deniedAt",
          modifier: "IS_NULL",
        },
        {
          type: "paidAt",
          modifier: "IS_NULL",
        },
        {
          type: "paymentDue",
          modifier: "LESS_THAN",
          value: now,
        },
      ] as const,
  },
};

export type OrderListSortType =
  | "created_at_asc"
  | "created_at_desc"
  | "dispatch_at_asc"
  | "dispatch_at_desc";

export const OrderListSortValues: SortType<
  OrderListSortModifierType,
  OrderListSortType
> = {
  created_at_desc: {
    key: "created_at_desc",
    name: "Most recent",
    mutation: {
      type: "createdAt",
      direction: "desc",
    },
  },
  created_at_asc: {
    key: "created_at_asc",
    name: "Least recent",
    mutation: {
      type: "createdAt",
      direction: "asc",
    },
  },
  dispatch_at_asc: {
    key: "dispatch_at_asc",
    name: "Orders to be shipped (soonest)",
    mutation: {
      type: "dispatchedPlannedAt",
      direction: "asc",
    },
  },
  dispatch_at_desc: {
    key: "dispatch_at_desc",
    name: "Orders to be shipped (latest)",
    mutation: {
      type: "dispatchedPlannedAt",
      direction: "desc",
    },
  },
};

export type OrderContainsType = "" | "preorder" | "regular";

export const OrderContainsValues: FilterType<
  OrderListParameterModifierType_supplier,
  OrderContainsType
> = {
  "": {
    key: "",
    name: "Any product",
    mutation: () => [],
  },
  preorder: {
    key: "preorder",
    name: "Pre-order product",
    mutation: () =>
      [
        {
          type: "latestAvailableFrom",
          modifier: "NOT_NULL",
        },
      ] as const,
  },
  regular: {
    key: "regular",
    name: "Regular product",
    mutation: () =>
      [
        {
          type: "latestAvailableFrom",
          modifier: "IS_NULL",
        },
      ] as const,
  },
};

export type PaymentTypeType =
  | ""
  | Exclude<OrderPaymentType, "stripe" | "cardPayment">
  | "paymentLink";

export const OrderPaymentTypeValues: FilterType<
  OrderListParameterModifierType_supplier,
  Exclude<PaymentTypeType, "cardPayment">
> = {
  "": {
    key: "",
    name: "All types",
    mutation: () => [],
  },
  sellarCredit: {
    key: "sellarCredit",
    name: "Sellar Credit",
    mutation: () =>
      [
        {
          type: "paymentType",
          modifier: "EQUALS",
          value: "sellarCredit",
        },
      ] as const,
  },
  checkout: {
    key: "checkout",
    name: "Card payment - checkout",
    mutation: () =>
      [
        {
          type: "paymentType",
          modifier: "EQUALS",
          value: "checkout",
        },
      ] as const,
  },
  manual: {
    key: "manual",
    name: "Direct customer payment",
    mutation: () =>
      [
        {
          type: "paymentType",
          modifier: "EQUALS",
          value: "manual",
        },
        {
          type: "hasPaymentLink",
          modifier: "EQUALS",
          value: false,
        },
      ] as const,
  },
  paymentLink: {
    key: "paymentLink",
    name: "Card payment link",
    mutation: () =>
      [
        {
          type: "hasPaymentLink",
          modifier: "EQUALS",
          value: true,
        },
      ] as const,
  },
  cardPaymentWithDeposit: {
    key: "cardPaymentWithDeposit",
    name: "Card payment - checkout deposit",
    mutation: () =>
      [
        {
          type: "paymentType",
          modifier: "EQUALS",
          value: "cardPaymentWithDeposit",
        },
      ] as const,
  },
};
